import { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'gatsby-plugin-intl';
import ReactHtmlParser from 'react-html-parser';

import { PreventDefault, StopPropagation } from '../../utils/events';
import Image from '../Image';
import CtaTextLink from '../CtaTextLink';
import IconDirections from '../../images/icon-directions.svg';

import {
  StoreInfo,
  StoreInfoButton,
  StoreInfoWrapper,
  LogoContainer,
  Logo,
  StoreDistance,
  StoreName,
  StoreAddress,
  CtaContainer,
  Drawer,
  StoreNumber,
  StoreHours,
} from './styles';

const StoreInfoCard = ({
  data,
  id,
  store,
  searchTerms,
  logos,
  selectedStore,
  setSelectedStore,
  marker,
  drawerOpen,
  setDrawerOpen,
  themes,
}) => {
  const { getDirectionsText, ctaLocationDetails } = data;
  const intl = useIntl();
  const [logo, setLogo] = useState(null);
  const [storeHours, setStoreHours] = useState(null);

  const { Distance, Name, Address1, City, State, PostCode, PhoneNumber } =
    store;

  // Determine which logo to use on each store
  useEffect(() => {
    const categories = store.CategoryNames.toLowerCase();
    const foundStore = searchTerms.find(
      ({ search }) => categories.indexOf(search) > 0
    );
    const logoKey = foundStore ? foundStore.logo : 'Loblaws';

    const foundLogo = logos.find(
      ({ storeLogoDam }) => storeLogoDam.altText === logoKey
    );

    setLogo(foundLogo);
  }, []);

  // Format the hours for locale and line breaks
  useEffect(() => {
    let hours = store.BusinessHours;

    if (intl.locale === 'fr') {
      hours = hours.replace('Mon', 'Lun');
      hours = hours.replace('Tues', 'Mar');
      hours = hours.replace('Wed', 'Merc');
      hours = hours.replace('Thu', 'Jeu');
      hours = hours.replace('Fri', 'Ven');
      hours = hours.replace('Sat', 'Sam');
      hours = hours.replace('Sun', 'Dim');
    }

    setStoreHours(hours.split('|'));
  }, []);

  // Set up store listings links
  const storeLocation = `https://www.google.com/maps/place/${Address1} ${City} ${State} ${PostCode}/`;
  const storeLocationFormatted = ReactHtmlParser(
    `${Address1}<br/>${City},&nbsp;${State} ${PostCode}`
  );
  const googleMapsUrl = storeLocation.split(' ').join('+');
  const getDirections = getDirectionsText || '';
  const directions = getDirections.replace('&nbsp;', ' ');

  const LocationDetails = () => {
    return (
      <>
        {/* Store hours and phone number drop down */}
        <Drawer open={drawerOpen}>
          <StoreHours phoneNumber={PhoneNumber}>
            {storeHours &&
              storeHours.map((h) => (
                <Fragment key={h}>
                  <p>{h}</p>
                </Fragment>
              ))}
          </StoreHours>
          {PhoneNumber && (
            <StoreNumber
              onClick={StopPropagation()}
              href={`tel:${PhoneNumber.replace(/\D/g, '')}`}
            >
              {intl.locale === 'fr' ? `Téléphoner ` : `Phone `}
              {PhoneNumber}
            </StoreNumber>
          )}
        </Drawer>
      </>
    );
  };

  return (
    <StoreInfo id={id} active={marker && marker.id === selectedStore}>
      {/* Desktop version - fully clickable */}
      <StoreInfoButton
        onClick={PreventDefault(() => {
          if (typeof window !== 'undefined') {
            // Set markerType as coming from results list, don't scroll results list
            const markerType = 'result';

            // Open store marker on map and set selected store
            window.google.maps.event.trigger(marker, 'click', markerType);
            setSelectedStore(store);
            setDrawerOpen(true);
          }
        })}
      >
        <LogoContainer>
          <Logo>
            {logo && (
              <Image
                damImage={
                  themes === 'dark'
                    ? logo.storeLogoDarkModeDam
                    : logo.storeLogoDam
                }
              />
            )}
          </Logo>
          <StoreDistance>{`${Distance}km`}</StoreDistance>
        </LogoContainer>
        <StoreName>
          <p>
            <strong>{Name}</strong>
          </p>
          <StoreAddress>{storeLocationFormatted}</StoreAddress>
        </StoreName>
        <LocationDetails />
      </StoreInfoButton>

      {/* Mobile version - internal clickable links */}
      <StoreInfoWrapper>
        <LogoContainer>
          <Logo>
            {logo && (
              <Image
                damImage={
                  themes === 'dark'
                    ? logo.storeLogoDarkModeDam
                    : logo.storeLogoDam
                }
              />
            )}
          </Logo>
          <StoreDistance>{`${Distance}km`}</StoreDistance>
        </LogoContainer>
        <StoreName>
          <p>
            <strong>{Name}</strong>
          </p>
          <CtaContainer>
            <CtaTextLink
              variant="black"
              target="_blank"
              rel="noopener noreferrer"
              url={googleMapsUrl}
              ariaLabel={directions}
              title={directions}
              className="address"
              themes={themes}
            >
              {storeLocationFormatted}
              <div className="directions-icon">
                <img src={IconDirections} alt="" role="presentation" />
              </div>
            </CtaTextLink>
          </CtaContainer>
          {ctaLocationDetails && (
            <CtaContainer>
              <CtaTextLink
                variant={ctaLocationDetails.style}
                ariaLabel={ctaLocationDetails.ariaLabel}
                onClick={PreventDefault(() => {
                  setDrawerOpen(!drawerOpen);
                })}
                themes={themes}
              >
                {ctaLocationDetails.linkTextMarkdown.linkTextMarkdown}
              </CtaTextLink>
            </CtaContainer>
          )}
        </StoreName>
        <LocationDetails />
      </StoreInfoWrapper>
    </StoreInfo>
  );
};

StoreInfoCard.propTypes = {
  data: PropTypes.shape().isRequired,
  id: PropTypes.string.isRequired,
  store: PropTypes.shape().isRequired,
  selectedStore: PropTypes.number,
  setSelectedStore: PropTypes.func.isRequired,
  marker: PropTypes.shape(),
  drawerOpen: PropTypes.bool.isRequired,
  setDrawerOpen: PropTypes.func.isRequired,
  searchTerms: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  logos: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  themes: PropTypes.string.isRequired,
};

StoreInfoCard.defaultProps = {
  selectedStore: null,
  marker: null,
};

export default StoreInfoCard;
